import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, BarChart3, X, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function MarketSummary() {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMovers, setShowMovers] = useState(null); // 'advancing' | 'declining' | null
  const [moversData, setMoversData] = useState(null);
  const [moversLoading, setMoversLoading] = useState(false);

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/market/summary`);
      const data = await response.json();
      if (data.success) {
        setMarketData(data);
      }
    } catch (err) {
      console.error('Error fetching market data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMovers = async (type) => {
    setShowMovers(type);
    if (moversData) return; // already loaded
    setMoversLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/market/movers`);
      const data = await response.json();
      if (data.success) {
        setMoversData(data);
        // Update market summary with real counts
        setMarketData(prev => prev ? {
          ...prev,
          advancing: data.total_advancing,
          declining: data.total_declining,
          unchanged: data.total_unchanged
        } : prev);
      }
    } catch (err) {
      console.error('Error fetching movers:', err);
    } finally {
      setMoversLoading(false);
    }
  };

  const formatVolume = (vol) => {
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
    return vol?.toString() || '0';
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-900/20 to-green-900/20 border border-border rounded-lg p-4 mb-4 animate-pulse">
        <div className="h-16 bg-card-hover rounded"></div>
      </div>
    );
  }

  if (!marketData) return null;

  const isPositive = marketData.asx200_change >= 0;
  const currentList = showMovers === 'advancing' ? moversData?.advancing : moversData?.declining;

  return (
    <>
      <div className="bg-gradient-to-r from-blue-900/20 to-green-900/20 border border-border rounded-lg p-4 mb-4" data-testid="market-summary">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* ASX 200 */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">S&P/ASX 200</div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold" data-testid="asx200-value">{marketData.asx200_value?.toLocaleString() || 'N/A'}</span>
                <span className={`flex items-center text-sm font-semibold ${isPositive ? 'text-green-500' : 'text-red-500'}`} data-testid="asx200-change">
                  {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                  {isPositive ? '+' : ''}{marketData.asx200_points_change?.toFixed(1)} pts ({isPositive ? '+' : ''}{marketData.asx200_change?.toFixed(2)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Market Stats - Clickable */}
          <div className="flex items-center gap-6 text-sm">
            <button
              onClick={() => fetchMovers('advancing')}
              className="text-left hover:bg-green-500/10 rounded-lg px-3 py-1.5 transition-colors cursor-pointer"
              data-testid="advancing-btn"
            >
              <div className="text-xs text-muted-foreground">Advancing</div>
              <div className="font-semibold text-green-500 flex items-center gap-1">
                {marketData.advancing || 0}
                <ArrowUpRight className="w-3 h-3" />
              </div>
            </button>
            <button
              onClick={() => fetchMovers('declining')}
              className="text-left hover:bg-red-500/10 rounded-lg px-3 py-1.5 transition-colors cursor-pointer"
              data-testid="declining-btn"
            >
              <div className="text-xs text-muted-foreground">Declining</div>
              <div className="font-semibold text-red-500 flex items-center gap-1">
                {marketData.declining || 0}
                <ArrowDownRight className="w-3 h-3" />
              </div>
            </button>
            <div>
              <div className="text-xs text-muted-foreground">Unchanged</div>
              <div className="font-semibold text-muted-foreground">{marketData.unchanged || 0}</div>
            </div>
          </div>

          {/* Market Status */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${marketData.market_open ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="text-xs text-muted-foreground">
              {marketData.market_open ? 'Market Open' : 'Market Closed'}
            </span>
          </div>
        </div>
      </div>

      {/* Movers Popup */}
      {showMovers && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowMovers(null)} data-testid="movers-overlay">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="movers-popup">
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b border-border ${showMovers === 'advancing' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              <div className="flex items-center gap-3">
                {showMovers === 'advancing' ? (
                  <TrendingUp className="w-5 h-5 text-green-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500" />
                )}
                <h2 className="text-lg font-bold">
                  {showMovers === 'advancing' ? 'Advancing' : 'Declining'} Stocks
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {/* Tab switcher */}
                <button
                  onClick={() => setShowMovers('advancing')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${showMovers === 'advancing' ? 'bg-green-500/20 text-green-400' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="tab-advancing"
                >
                  Advancing
                </button>
                <button
                  onClick={() => setShowMovers('declining')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${showMovers === 'declining' ? 'bg-red-500/20 text-red-400' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="tab-declining"
                >
                  Declining
                </button>
                <button onClick={() => setShowMovers(null)} className="p-1 hover:bg-card-hover rounded-md ml-2" data-testid="close-movers">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(80vh-64px)]">
              {moversLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading stock data...</span>
                </div>
              ) : currentList && currentList.length > 0 ? (
                <table className="w-full">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left p-3">#</th>
                      <th className="text-left p-3">Symbol</th>
                      <th className="text-right p-3">Price</th>
                      <th className="text-right p-3">Change</th>
                      <th className="text-right p-3">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentList.map((stock, idx) => (
                      <tr key={stock.symbol} className="border-b border-border/50 hover:bg-card-hover transition-colors" data-testid={`mover-row-${stock.symbol}`}>
                        <td className="p-3 text-xs text-muted-foreground">{idx + 1}</td>
                        <td className="p-3 font-semibold text-sm">{stock.symbol}</td>
                        <td className="p-3 text-right text-sm">${stock.price?.toFixed(2)}</td>
                        <td className={`p-3 text-right text-sm font-semibold ${stock.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct?.toFixed(2)}%
                        </td>
                        <td className="p-3 text-right text-sm text-muted-foreground">{formatVolume(stock.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  No {showMovers} stocks found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MarketSummary;
